import React from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  ButtonProps,
  Flex,
  Icon,
  Portal,
  Text,
} from '@chakra-ui/react';
import { MdExpandMore } from 'react-icons/md';
import { Scrollbars } from 'react-custom-scrollbars-2';

export interface DropdownItem {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  onClick?: () => void;
  isDisabled?: boolean;
  isDanger?: boolean;
}

export interface DropdownProps extends Omit<ButtonProps, 'onSelect'> {
  label?: string;
  items: DropdownItem[];
  onSelect?: (value: string | number) => void;
  showIcon?: boolean;
  icon?: React.ReactNode;
  borderRadius?: string | number;
  padding?: string | number;
  listMaxH?: string | number;
  /** Custom trigger element — replaces the default Button trigger */
  trigger?: React.ReactNode;
  /** Padding horizontal de cada item */
  itemPx?: string | number;
  /** Padding vertical de cada item */
  itemPy?: string | number;
  /** Tamaño de la tipografía de los items */
  itemFontSize?: string | number;
  /** Color base del label e icono de los items */
  itemColor?: string;
  /** Color al hacer hover sobre el item */
  itemHoverColor?: string;
  /** Ancho del dropdown */
  menuWidth?: string | number;
}

export const Dropdown: React.FC<DropdownProps> = ({
  label = 'Opciones',
  items,
  onSelect,
  showIcon = true,
  icon,
  borderRadius = '7px',
  padding = '25px 25px',
  listMaxH,
  trigger,
  itemPx = '14px',
  itemPy = '10px',
  itemFontSize = 'sm',
  itemColor,
  itemHoverColor,
  menuWidth,
  ...buttonProps
}) => {
  const handleItemClick = (item: DropdownItem) => {
    if (item.onClick) item.onClick();
    onSelect?.(item.value);
  };

  const menuItems = items.map((item, index) => (
    <React.Fragment key={item.value}>
      <MenuItem
        onClick={() => handleItemClick(item)}
        isDisabled={item.isDisabled}
        color={item.isDanger ? 'red.500' : (itemColor ?? 'inherit')}
        transition='color 0.15s ease'
        _hover={item.isDanger ? { bg: 'red.50', color: 'red.600' } : { color: itemHoverColor ?? 'brand.500' }}
        px={itemPx}
        py={itemPy}
        fontSize={itemFontSize}
      >
        <Flex align='center' gap='10px'>
          {item.icon}
          {item.label}
        </Flex>
      </MenuItem>
      {!listMaxH && index < items.length - 1 && <MenuDivider m="0" />}
    </React.Fragment>
  ));

  return (
    <Menu>
      {trigger ? (
        <MenuButton p='0px' background='none' _hover={{ opacity: 0.7 }} transition='opacity 0.2s ease' {...buttonProps}>
          {trigger}
        </MenuButton>
      ) : (
        <MenuButton
          as={Button}
          rightIcon={showIcon ? icon || <Icon as={MdExpandMore} /> : undefined}
          borderRadius={borderRadius}
          overflow="hidden"
          {...buttonProps}
        >
          <Text as="span" isTruncated display="block">{label}</Text>
        </MenuButton>
      )}
      <Portal>
        <MenuList borderRadius={borderRadius} overflow="hidden" p="0" w={menuWidth} minW={menuWidth ?? 'auto'} zIndex="popover">
          {listMaxH ? (
            <Scrollbars
              autoHeight
              autoHeightMax={listMaxH}
              renderTrackVertical={({ style, ...props }) => (
                <div
                  {...props}
                  style={{ ...style, width: 5, right: 3, top: 4, bottom: 4, borderRadius: 4, background: 'rgba(0,0,0,0.08)' }}
                />
              )}
              renderThumbVertical={({ style, ...props }) => (
                <div
                  {...props}
                  style={{ ...style, borderRadius: 4, background: 'rgba(0,0,0,0.38)', cursor: 'pointer' }}
                />
              )}
              renderView={({ style, ...props }) => (
                <div {...props} style={{ ...style, marginBottom: 0 }} />
              )}
            >
              {menuItems}
            </Scrollbars>
          ) : (
            menuItems
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default Dropdown;
